import DriveHeader from '@/components/DriveHeader.tsx'
import { Card, Loading } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useDrives } from '@/stores/useDrives.ts'
import { useQuery } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import HeyDriveStorageIncreaseNote, { HeyDriveStorageIncreaseReason } from '@/models/storageIncreaseNote.ts'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function StorageDurationPage() {
  const { currentDrive } = useDrives()
  const { getIncreaseNotes, initStorageUpgrade } = useClient()
  const [isLoadingStripe, setIsLoadingStripe] = useState(false)
  const { t } = useTranslation()

  if (!currentDrive) {
    throw 'No drive'
  }

  const drive = currentDrive

  const { data: increaseNotes, isLoading } = useQuery({
    queryKey: ['drive', drive.id, 'storage_increase'],
    queryFn: async () => {
      const res = await getIncreaseNotes(drive.id)
      return res.data as {storage_increases: Array<HeyDriveStorageIncreaseNote>}
    },
  })

  if (isLoadingStripe) {
    return
  }

  async function onUpgradeClick(months: number) {
    setIsLoadingStripe(true)
    try {
      const res = await initStorageUpgrade(drive, months)
      const data: {
        session_id: string
        stripe_session_id: string
        checkout_url: string
        status: string
        expires_at: string
      } = res.data
      window.location.href = data.checkout_url
    } catch (e) {
      setIsLoadingStripe(false)
      alert(t('common.paymentErrorContactSupport'))
    }
  }

  function getLabelForReason(reason: HeyDriveStorageIncreaseReason) {
    return {
      [HeyDriveStorageIncreaseReason.PRE_ORDER]: t('storageDuration.purchase'),
      [HeyDriveStorageIncreaseReason.COUPON_USAGE]: t('storageDuration.referral'),
      [HeyDriveStorageIncreaseReason.PURCHASE]: t('storageDuration.purchase'),
      [HeyDriveStorageIncreaseReason.OTHER]: t('storageDuration.other'),
    }[reason]
  }

  return <div>
    <DriveHeader />
    <div className="container mx-auto flex flex-col gap-3 justify-center items-center py-5 px-3">
      <h2 className='font-serif text-3xl mb-3'>
        {t('common.storageDuration')}
      </h2>
      { !drive.usage_starts_at && <p>✅ {t('common.noStorageDesc')}</p>}
      { drive.usage_starts_at && <>
        <p>{t('common.usageStarts', {date: new Date(drive.usage_starts_at).toLocaleDateString()})}</p>
      </>}
      { drive.expires_at && <>
        <p>{t('common.storageEnds', {date: new Date(drive.expires_at).toLocaleDateString()})}</p>
      </>}
      <h2 className='font-serif text-2xl mb-1 mt-3'>
        {t('common.increaseStorage')}
      </h2>
      <div className='flex flex-row gap-3 w-full'>
        <Card className='flex-1'>
          <Card.Body className='flex-1 p-3'>
            <span className='font-bold text-lg'>+12 {t('common.months')}</span>
            <p className='text-sm'>{t('common.monthsOfStorage', {months: '12'})}</p>
            <Button onClick={() => onUpgradeClick(12)} color='primary'>15€ {t('common.buy')}</Button>
          </Card.Body>
        </Card>

        <Card className='flex-1'>
          <Card.Body className='flex-1 p-3'>
            <span className='font-bold text-lg'>+24 {t('common.months')}</span>
            <p className='text-sm'>{t('common.monthsOfStorage', {months: '24'})}</p>
            <Button onClick={() => onUpgradeClick(24)} color='primary'>30€ {t('common.buy')}</Button>
          </Card.Body>
        </Card>
      </div>
      <h2 className='font-serif text-2xl mb-1 mt-3'>
        {t('common.yourStorageExtensions')}
      </h2>
      { isLoading && <Loading color='primary' />}
      { !isLoading && <div className='flex flex-col w-full'>
        { increaseNotes?.storage_increases.length ? <div className='flex flex-col gap-3 w-full'>
          {increaseNotes.storage_increases.map(note => <div className='bg-secondary rounded-2xl p-5 w-full'>
            <p className='font-bold text-1xl'>{getLabelForReason(note.reason)}</p>
            <p>+{note.more_months} {t('common.months')}</p>
          </div>)}
        </div> : <p>{t('common.noExpiry')}</p>}
      </div>}
    </div>
  </div>
}
